<template>
  <div class="opc-cart__next">
    <div v-for="totalItem in totalPanelList" :key="totalItem.billingCycle">
      <h3 class="opc-cart__next__title" :class="[(isInrCitEligible || $context.is.delta) ? 'opc-cart__next__title__preview' : '']">
        {{ totalItem.label }}
        <div class="opc-cart__next__value text-no-capitalize" :class="[(isInrCitEligible || $context.is.delta) ? 'opc-cart__next__value__preview' : '']">{{ $money(totalItem.amount) }}</div>
      </h3>
      <p class="opc-cart__next__text first-capitalize">{{ totalItem.date }}</p>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { BILLING_CYCLE, DEFAULT_FREE_TRIAL_DAYS, planTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import _ from 'lodash';
import { pageConfig } from '@/common/service/page-config';

export const dateformat = 'MMM D, YYYY';

export const span = {
  [BILLING_CYCLE.MONTHLY]: [1, 'month'],
  [BILLING_CYCLE.ANNUAL]: [1, 'year'],
  [BILLING_CYCLE.QUARTERLY]: [1, 'quarter'],
  [BILLING_CYCLE.TWO_YEARS]: [2, 'year'],
  [BILLING_CYCLE.THREE_YEARS]: [3, 'year'],
};

export default {
  methods: {
    // nextAmount(cycle) {
    //   const isRecurring = [BILLING_CYCLE.ANNUAL, BILLING_CYCLE.MONTHLY, BILLING_CYCLE.QUARTERLY].includes(cycle);
    //   if (!isRecurring) return 0;
    //   const plans = this.getSelectedPlans.filter((x) => x.billing_cycle === cycle);
    //   const amount = plans.reduce((acc, x) => ((acc += x.amount), acc), 0);
    //   return amount;
    // },
    nextDate(startTime, cycle) {
      let time;
      // when monthly base plan,will evergreen = true, term = 100, so should choose latest time between {contract term date} and {cycle date}
      let evergreen = false;
      if (this.$context.is.edit) {
        //1.when smb customer product term and contract term are different, downgrade/downsell should be executed based on the contract term
        const smbShouldFollowTermEndDate = this.basicConfig?.isSMBCustomer
          && this.basicConfig?.term !== this.currentEditPlan?.cycleValue
          && (this.isCurrentEditDownSell || this.isCurrentMeetingPlanDowngrade);
        //2.when customer biz>pro, biz plus>biz, downgrade should be executed based on the contract term
        if (smbShouldFollowTermEndDate || this.isCurrentMeetingPlanDowngrade) {
          time = moment(new Date(this.basicConfig?.termEndDate)).format(dateformat);
          //if evergreen === true
          if (this.basicConfig?.evergreen === true) {
            evergreen = true;
          }
        }else {
          //if pending down sell
          if (this.isCurrentEditDownSell) {
            if (pageConfig?.editConfig?.planType === planTypes.CMR) {
              startTime = new Date(this.currentEditPlan?.cartInfo?.planStartTime || new Date());
            } else {
              time = moment(new Date(this.currentEditPlan?.cartInfo?.planExpireTime)).format(dateformat);
            }
          }
        }
        //normal edit
        startTime = new Date(this.currentEditPlan?.cartInfo?.planStartTime || new Date());
      } else if (this.$context.is.add2cart || this.$context.is.trial2paid || this.$context.is.zs2ze) {
        // FREE TRIAL - trial2paid flow, whatever the plan is, even the plan is relative independent like Scheduler, its charge date would be still same as the base plan(Pro/Biz > Phone...) if the user had bought before.
        //this.getCurrentBasePlanInfo?.plans[0]?.cartInfo?.planStartTime may is null
        let planStartTime = this.getCurrentBasePlanInfo?.plans[0]?.cartInfo?.planStartTime;
        if (!planStartTime) {
          planStartTime = this.boughtPlans.find((p) => p.plan_code === this.getCurrentBasePlanInfo?.plans[0]?.plan_code)?.cartInfo?.planStartTime
            //for only buy standalone plan, then add2card
            || this.boughtPlans[0]?.cartInfo?.planStartTime
            || new Date();
        }
        startTime = new Date(planStartTime);
      }

      if (!span[cycle]) return '';

      let expireTime = moment(startTime)
        .add(...span[cycle])
        .format(dateformat);

      if (evergreen && time && expireTime) {
        time = _.sortBy([time, expireTime], d => new Date(d))[0];
      }

      time = time ?? expireTime;
      return `${this.$t('billing.order_summary.next_charge_date')} ${time}`;
    },
    // FREE TRIAL - after trial days
    nextAfterTrialChargeDate(startTime, trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      const time = moment(startTime)
        .add(trialDays, 'days')
        .format(dateformat);
      return `${this.$t('billing.order_summary.next_charge_date')} ${time}`;
    }
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'nextBillAmount',
      'getCycledTaxTotalAmount',
      'discountWithAmountMap',
      'currentEditPlan',
      'isCurrentEditDownSell',
      'isCurrentMeetingPlanDowngrade',
      'getCurrentBasePlanInfo',
      'boughtPlans',
      'basicConfig',
      'mixBuyFlowContainsEditCase',
      'migratePlanState',
      'isInrCitEligible',
    ]),
    totalPanelList() {
      const totalPanelBaseOnBillingCycle = [
        {
          billingCycle: BILLING_CYCLE.MONTHLY,
          amount: this.monthlyAmount,
          label: this.monthlyLabel,
          date: this.monthlyDate
        },
        {
          billingCycle: BILLING_CYCLE.ANNUAL,
          amount: this.annuallyAmount,
          label: this.annuallyLabel,
          date: this.annuallyDate
        },
        {
          billingCycle: BILLING_CYCLE.QUARTERLY,
          amount: this.quarterlyAmount,
          label: this.quarterlyLabel,
          date: this.quarterlyDate
        },
        {
          billingCycle: BILLING_CYCLE.TWO_YEARS,
          amount: this.twoYearsAmount,
          label: this.twoYearsLabel,
          date: this.twoYearsDate
        },
        {
          billingCycle: BILLING_CYCLE.THREE_YEARS,
          amount: this.threeYearsAmount,
          label: this.threeYearsLabel,
          date: this.threeYearsDate
        },
      ]
      return totalPanelBaseOnBillingCycle.filter(({ amount }) => amount > 0)
    },
    /**
     * Today’s total is, as a matter of fact, not a precise amount for charing the users.
     * e.g. The user’s subscription of $15.99/monthly begins on 2024/05/10,
     * if he adds 2 licenses on 2024/05/14,
     * then the final charge amount will be approximately
     * 15.99 * 2 * (30 - (14-10)) / 30.
     * Thats why, as I mentioned above, the today’s total has been through multiple changes
     * and finally updated to New Monthly Total to avoid legal issues.
     * (we are going to implement a preview API call from zuora in the next half of this year
     * which is expected to provide an accurate amount. I think, by then, it will be okay to
     * display today’s total with the exact amount).
     * -- Henry Yu 2024/05/14
     */
    monthlyLabel() {
      if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade) return this.$t('billing.order_summary.total_billed_monthly_new');
      if (this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.zs2ze || this.$context.is.add2cart) return this.$t('billing.order_summary.total_billed_monthly_new');
      return this.$t('billing.order_summary.total_billed_monthly');
    },
    annuallyLabel() {
      if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade) return this.$t('billing.order_summary.total_billed_annually_new');
      if (this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.zs2ze || this.$context.is.add2cart) return this.$t('billing.order_summary.total_billed_annually_new');
      return this.$t('billing.order_summary.total_billed_annually');
    },
    monthlyAmount() {
      return this.nextBillAmount?.monthlyCharge + this.getCycledTaxTotalAmount[BILLING_CYCLE.MONTHLY]??0;
    },
    monthlyDate() {
      if(this.isFreeTrialFlow) return this.nextAfterTrialChargeDate(new Date(), this.selectedFreeTrialPlan?.trialDays ?? DEFAULT_FREE_TRIAL_DAYS);
      return this.nextDate(new Date(), BILLING_CYCLE.MONTHLY);
    },
    annuallyAmount() {
      return this.nextBillAmount?.annualCharge + this.getCycledTaxTotalAmount[BILLING_CYCLE.ANNUAL]??0;
    },
    annuallyDate() {
      if(this.isFreeTrialFlow) return this.nextAfterTrialChargeDate(new Date(), this.selectedFreeTrialPlan?.trialDays ?? DEFAULT_FREE_TRIAL_DAYS);
      return this.nextDate(new Date(), BILLING_CYCLE.ANNUAL);
    },
    quarterlyLabel() {
      if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade || this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.zs2ze || this.$context.is.add2cart) return this.$t('billing.order_summary.total_billed_quarterly_new');
      return this.$t('billing.order_summary.total_billed_quarterly');
    },
    quarterlyAmount() {
      return this.nextBillAmount?.quarterlyCharge + this.getCycledTaxTotalAmount[BILLING_CYCLE.QUARTERLY]??0;
    },
    quarterlyDate() {
      return this.nextDate(new Date(), BILLING_CYCLE.QUARTERLY);
    },
    twoYearsLabel() {
      if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade || this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.zs2ze || this.$context.is.add2cart) return this.$t('billing.order_summary.total_billed_two_years_new');
      return this.$t('billing.order_summary.total_billed_two_years');
    },
    twoYearsAmount() {
      return this.nextBillAmount?.twoYearsCharge + this.getCycledTaxTotalAmount[BILLING_CYCLE.TWO_YEARS]??0;
    },
    twoYearsDate() {
      return this.nextDate(new Date(), BILLING_CYCLE.TWO_YEARS);
    },
    threeYearsLabel() {
      if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade || this.$context.is.pro2biz || this.$context.is.edit || this.mixBuyFlowContainsEditCase || this.$context.is.zs2ze || this.$context.is.add2cart) return this.$t('billing.order_summary.total_billed_three_years_new');
      return this.$t('billing.order_summary.total_billed_three_years');
    },
    threeYearsAmount() {
      return this.nextBillAmount?.threeYearsCharge + this.getCycledTaxTotalAmount[BILLING_CYCLE.THREE_YEARS]??0;
    },
    threeYearsDate() {
      return this.nextDate(new Date(), BILLING_CYCLE.THREE_YEARS);
    },
    mx_upgrade_nextChargeLabel() {
      const { totalDiscount = 0 } = this.discountWithAmountMap || {};
      // filter zp base plan (pay as you go)
      const getSelectedPlans = this.getSelectedPlans.filter(p => p.amount > 0);
      if (getSelectedPlans.every(x => x.billing_cycle === BILLING_CYCLE.MONTHLY) && !totalDiscount) return this.monthlyLabel;
      if (getSelectedPlans.every(x =>x.billing_cycle === BILLING_CYCLE.ANNUAL) && !totalDiscount) return this.annuallyLabel;
      if (getSelectedPlans.every(x =>x.billing_cycle === BILLING_CYCLE.QUARTERLY) && !totalDiscount) return this.quarterlyLabel;
      if (getSelectedPlans.every(x =>x.billing_cycle === BILLING_CYCLE.TWO_YEARS) && !totalDiscount) return this.twoYearsLabel;
      if (getSelectedPlans.every(x =>x.billing_cycle === BILLING_CYCLE.THREE_YEARS) && !totalDiscount) return this.threeYearsLabel;
      return this.$t('billing.order_summary.todays_total');
    },
    // BUY TRIAL
    // FREE TRIAL - next date changed to startday + trial days
    isFreeTrialFlow () {
      return this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    selectedFreeTrialPlan() {
      const hasTrialPlan = plansof(this.getSelectedPlans).filter(item => !!item.isOnlineFreeTrial).has();
      if(hasTrialPlan) {
        return plansof(this.getSelectedPlans).filter(item => !!item.isOnlineFreeTrial).get(0);
      }
      return null;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "next-scope";
</style>

<style lang="scss">
@import "next";
</style>
